import { IApplication, IPathName, IServiceProvider } from '@h3ravel/shared'

import { Container } from './Container'
import { PathLoader } from '@h3ravel/shared'
import dotenv from 'dotenv'
import path from 'node:path'

export class Application extends Container implements IApplication {
    paths = new PathLoader()
    private booted = false
    private versions = { app: '0', ts: '0' }
    private basePath: string

    private providers: IServiceProvider[] = []
    protected externalProviders: Array<new (_app: Application) => IServiceProvider> = []

    constructor(basePath: string) {
        super()
        this.basePath = basePath
        this.setPath('base', basePath)
        this.loadOptions()
        this.registerBaseBindings();
        dotenv.config({ quiet: true })
    }

    /**
     * Register core bindings into the container
     */
    protected registerBaseBindings () {
        this.bind(Application, () => this)
        this.bind('path.base', () => this.basePath)
        this.bind('load.paths', () => this.paths)
    }

    /**
     * Dynamically register all configured providers
     */
    public async registerConfiguredProviders () {
        const providers = await this.getAllProviders()

        for (const ProviderClass of providers) {
            if (!ProviderClass) continue
            const provider = new ProviderClass(this)
            await this.register(provider)
        }
    }

    protected async loadOptions () {
        const app = await this.safeImport(this.getPath('base', 'package.json'))
        const core = await this.safeImport('../package.json')

        if (app && app.dependencies) {
            this.versions.app = app.dependencies['@h3ravel/core']
        }
        if (core && core.devDependencies) {
            this.versions.ts = app.devDependencies.typescript
        }
    }

    /**
     * Load default and optional providers dynamically
     * 
     * Auto-Registration Behavior
     * 
     * Minimal App: Loads only core, config, http, router by default.
     * Full-Stack App: Installs database, mail, queue, cache → they self-register via their providers.
     */
    protected async getConfiguredProviders (): Promise<Array<new (_app: Application) => IServiceProvider>> {
        return [
            (await import('@h3ravel/core')).CoreServiceProvider,
            (await import('@h3ravel/core')).ViewServiceProvider,
        ]
    }

    protected async getAllProviders (): Promise<Array<new (_app: Application) => IServiceProvider>> {
        const coreProviders = await this.getConfiguredProviders();
        const allProviders = [...coreProviders, ...this.externalProviders];

        /**
         * Deduplicate by class reference
         */
        const uniqueProviders = Array.from(new Set(allProviders));

        return this.sortProviders(uniqueProviders);
    }

    private sortProviders (providers: Array<new (_app: Application) => IServiceProvider>) {
        const priorityMap = new Map<string, number>();

        /**
         * Base priority (default 0)
         */
        providers.forEach((Provider) => {
            priorityMap.set(Provider.name, (Provider as any).priority ?? 0);
        });

        /**
         * Handle before/after adjustments
         */
        providers.forEach((Provider) => {
            const order = (Provider as any).order;
            if (!order) return;

            const [direction, target] = order.split(':');
            const targetPriority = priorityMap.get(target) ?? 0;

            if (direction === 'before') {
                priorityMap.set(Provider.name, targetPriority - 1);
            } else if (direction === 'after') {
                priorityMap.set(Provider.name, targetPriority + 1);
            }
        });

        /**
         * Sort the service providers based on thier name and priority
         */
        const sorted = providers.sort(
            (A, B) => (priorityMap.get(B.name) ?? 0) - (priorityMap.get(A.name) ?? 0)
        );

        /**
         * If debug is enabled, let's show the loaded service provider info
         */
        if (process.env.APP_DEBUG === 'true') {
            console.table(
                sorted.map((P) => ({
                    Provider: P.name,
                    Priority: priorityMap.get(P.name),
                    Order: (P as any).order || 'N/A',
                }))
            );
        }

        return sorted
    }

    registerProviders (providers: Array<new (_app: Application) => IServiceProvider>): void {
        this.externalProviders.push(...providers)
    }

    /**
     * Register a provider
     */
    public async register (provider: IServiceProvider) {
        await provider.register()
        this.providers.push(provider)
    }

    /**
     * Boot all providers after registration
     */
    public async boot () {
        if (this.booted) return

        for (const provider of this.providers) {
            if (provider.boot) {
                await provider.boot()
            }
        }

        this.booted = true
    }

    /**
     * Attempt to dynamically import an optional module
     */
    private async safeImport (moduleName: string) {
        try {
            const mod = await import(moduleName)
            return mod.default ?? mod ?? {}
        } catch {
            return null
        }
    }

    /**
     * Get the base path of the app
     * 
     * @returns 
     */
    getBasePath (): string {
        return this.basePath
    }

    /**
     * Dynamically retrieves a path property from the class.
     * Any property ending with "Path" is accessible automatically.
     *
     * @param name - The base name of the path property
     * @returns 
     */
    getPath (name: IPathName, pth?: string) {
        return path.join(this.paths.getPath(name, this.basePath), pth ?? '')
    }

    /**
     * Programatically set the paths.
     *
     * @param name - The base name of the path property
     * @param path - The new path
     * @returns 
     */
    setPath (name: IPathName, path: string) {
        return this.paths.setPath(name, path, this.basePath)
    }

    /**
     * Returns the installed version of the system core and typescript.
     *
     * @returns 
     */
    getVersion (key: 'app' | 'ts') {
        return this.versions[key]?.replaceAll(/\^|~/g, '')
    }
}
